/**
 * Bind the Telegram BackButton to a callback for the lifetime of the
 * caller component.
 *
 * The SDK BackButton is a global, shared resource — many components may
 * try to control it, but only the latest mount wins. This hook does the
 * minimum amount of setup/teardown around that:
 *   1. ensure the button is mounted (no-op if it already is);
 *   2. show it;
 *   3. install the click handler;
 *   4. on unmount, remove the handler and hide the button again.
 *
 * Outside Telegram (e.g. local dev in a regular browser) the underlying
 * methods throw — `isBackButtonSupported()` returns false there. We
 * short-circuit so non-TG hosts keep working without a try/catch in every
 * caller.
 */
import { useEffect } from "react";
import { backButton } from "@telegram-apps/sdk-react";

export function useBackButton(onClick: () => void): void {
  useEffect(() => {
    if (!backButton.isSupported()) {
      return;
    }

    if (!backButton.isMounted()) {
      backButton.mount();
    }
    backButton.show();
    const off = backButton.onClick(onClick);

    return () => {
      off();
      backButton.hide();
    };
  }, [onClick]);
}
